import { FirebaseOptions, getApp, getApps, initializeApp } from 'firebase/app';
import { Auth, browserLocalPersistence, getAuth, setPersistence } from 'firebase/auth';

const getEnvValue = (key: string) => process.env[key]?.trim();

const firebaseEnv = {
  REACT_APP_FIREBASE_API_KEY: getEnvValue('REACT_APP_FIREBASE_API_KEY'),
  REACT_APP_FIREBASE_AUTH_DOMAIN: getEnvValue('REACT_APP_FIREBASE_AUTH_DOMAIN'),
  REACT_APP_FIREBASE_PROJECT_ID: getEnvValue('REACT_APP_FIREBASE_PROJECT_ID'),
  REACT_APP_FIREBASE_APP_ID: getEnvValue('REACT_APP_FIREBASE_APP_ID'),
  REACT_APP_FIREBASE_STORAGE_BUCKET: getEnvValue('REACT_APP_FIREBASE_STORAGE_BUCKET'),
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID: getEnvValue(
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID'
  ),
};

export const missingFirebaseConfigKeys = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_APP_ID',
].filter((key) => !firebaseEnv[key as keyof typeof firebaseEnv]);

export const isFirebaseConfigured = missingFirebaseConfigKeys.length === 0;

export const shouldSendFirebaseIdToken =
  getEnvValue('REACT_APP_SEND_FIREBASE_ID_TOKEN') === 'true';

const firebaseConfig: FirebaseOptions = {
  apiKey: firebaseEnv.REACT_APP_FIREBASE_API_KEY as string,
  authDomain: firebaseEnv.REACT_APP_FIREBASE_AUTH_DOMAIN as string,
  projectId: firebaseEnv.REACT_APP_FIREBASE_PROJECT_ID as string,
  appId: firebaseEnv.REACT_APP_FIREBASE_APP_ID as string,
  ...(firebaseEnv.REACT_APP_FIREBASE_STORAGE_BUCKET
    ? { storageBucket: firebaseEnv.REACT_APP_FIREBASE_STORAGE_BUCKET }
    : {}),
  ...(firebaseEnv.REACT_APP_FIREBASE_MESSAGING_SENDER_ID
    ? { messagingSenderId: firebaseEnv.REACT_APP_FIREBASE_MESSAGING_SENDER_ID }
    : {}),
};

let authInstance: Auth | null = null;
let persistencePromise: Promise<void> | null = null;

export const getFirebaseAuth = (): Auth | null => {
  if (!isFirebaseConfigured) {
    return null;
  }

  if (!authInstance) {
    const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
    authInstance = getAuth(app);
    persistencePromise =
      persistencePromise ||
      setPersistence(authInstance, browserLocalPersistence).catch((error) => {
        console.warn('Failed to set Firebase auth persistence.', error);
      });
  }

  return authInstance;
};

export const ensureFirebaseAuth = async (): Promise<Auth> => {
  const auth = getFirebaseAuth();

  if (!auth) {
    throw new Error(
      'Firebase の環境変数が不足しています。.env.local または Vercel の設定を確認してください。'
    );
  }

  if (persistencePromise) {
    await persistencePromise;
  }

  return auth;
};
